// Context handed to the configurator by whatever launches it (e.g. a NetSuite
// script that opens this app for a specific customer/transaction). Read from URL
// query params, with a `window.__CPQ_CONTEXT__` object as a fallback so an
// embedding page can inject it without a redirect.
//
//   ?customerid=<internalId>    -> the customer for the Customer Part Number record
//   ?tranid=<id>                -> current transaction id (sales order; blank when unsaved)
//   ?transaction2=<id>          -> a second transaction id (reserved for a future ask)
//   ?line=<n>                   -> 1-based sales-order line ("Edit Config"); absent for "Configure"
//   ?itemid=<id or part no>     -> the line's item ("Edit Config"): cpq2 auto-loads its saved config
//   ?cpn=<part number>          -> the line's Customer Part Number ("Edit Config"): pre-fills the CPN field
//   ?custbody101 / ?custbody102 -> the order's two config body fields (echoed back)
//   ?userid / ?username         -> the launching NetSuite operator (Favorites "Created by")
//   ?trantype                   -> launching record type (salesorder/opportunity/estimate)
//   ?customform                 -> the launching record's form id (O-Type / Box Type forms open
//                                  Natural Draft Burners directly; see products.json "forms")

export type LaunchContext = {
  customerId: string | null;
  transactionId: string | null;
  transactionId2: string | null;
  // 1-based sales-order line when launched from "Edit Config"; null for "Configure" (new line).
  line: number | null;
  // The line's item (internal id or part number) when launched from "Edit Config";
  // cpq2 auto-loads that item's saved configuration on start. null for "Configure".
  itemId: string | null;
  // The launched sales-order line's Customer Part Number ("Edit Config"), used to
  // pre-fill the cpq2 CPN field. null for "Configure" (or a line with no CPN).
  customerPartNumber: string | null;
  custbody101: string | null;
  custbody102: string | null;
  // Customer display name (from the launching record's entity), for Favorites.
  customerName: string | null;
  // Launching NetSuite operator — the "Created by" for saved Favorites.
  userId: string | null;
  userName: string | null;
  // Origin type of the launching transaction, normalized to SO / Opp / Quo.
  originType: string | null;
  // The launching record's form (NetSuite customform internal id), or null.
  formId: string | null;
};

declare global {
  interface Window {
    __CPQ_CONTEXT__?: Partial<{
      customerId: string;
      transactionId: string;
      transactionId2: string;
      line: string;
      itemId: string;
      customerPartNumber: string;
      custbody101: string;
      custbody102: string;
      customerName: string;
      userId: string;
      userName: string;
      originType: string;
      formId: string;
    }>;
  }
}

// NetSuite record type -> friendly origin type used by Favorites.
function mapOriginType(t: string | null): string | null {
  if (!t) return null;
  const s = t.trim().toLowerCase();
  if (s === "salesorder" || s === "so") return "SO";
  if (s === "opportunity" || s === "opp") return "Opp";
  if (s === "estimate" || s === "quote" || s === "quo") return "Quo";
  return t; // unknown -> pass through as-is
}

let cached: LaunchContext | null = null;

export function getLaunchContext(): LaunchContext {
  if (cached) return cached;
  const p =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const g = (typeof window !== "undefined" && window.__CPQ_CONTEXT__) || {};
  const pick = (keys: string[], gk: keyof typeof g): string | null => {
    for (const k of keys) {
      const v = p.get(k);
      if (v) return v;
    }
    return (g[gk] as string | undefined) ?? null;
  };
  const lineRaw = pick(["line"], "line");
  const lineNum = lineRaw != null && /^\d+$/.test(lineRaw) ? Number(lineRaw) : null;
  cached = {
    customerId: pick(["customer", "customerId", "customerid"], "customerId"),
    transactionId: pick(["transaction", "transactionId", "tranid", "txn"], "transactionId"),
    transactionId2: pick(["transaction2", "transactionId2", "txn2"], "transactionId2"),
    line: lineNum,
    itemId: pick(["itemid", "item", "itemId"], "itemId"),
    customerPartNumber: pick(["cpn", "customerPartNumber"], "customerPartNumber"),
    custbody101: pick(["custbody101"], "custbody101"),
    custbody102: pick(["custbody102"], "custbody102"),
    customerName: pick(["customername", "customerName"], "customerName"),
    userId: pick(["userid", "userId"], "userId"),
    userName: pick(["username", "userName"], "userName"),
    originType: mapOriginType(pick(["trantype", "tranType", "originType"], "originType")),
    formId: pick(["customform", "form", "formId"], "formId"),
  };
  return cached;
}

function isDevHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

/**
 * Access gate. cpq2 may only run if it was opened by another window — i.e.
 * window.opener is set. The Configure / Edit Config buttons open cpq2 with
 * window.open(...), so a real launch always has an opener. This is the same
 * signal that shows the "Cancel" button (openedFromSalesOrder in
 * salesOrderBridge): no opener -> no Cancel button -> no access. A bookmarked
 * or shared link opened in a fresh tab has no opener and is blocked. localhost
 * is always allowed for development.
 *
 * NOTE: this stops casual link-sharing (a pasted link, or a link clicked in
 * email/chat, opens with no opener). It is NOT tamper-proof — a page that
 * itself calls window.open on a launch URL would pass. Blocking that too means
 * verifying the opener's ORIGIN via a postMessage handshake, which requires a
 * matching responder deployed in the NetSuite client script.
 */
export function isOpenedFromNetsuite(): boolean {
  if (isDevHost()) return true;
  if (typeof window === "undefined") return false;
  return !!window.opener && window.opener !== window;
}
